// ─────────────────────────────────────────────────────────────────────────────
// CrewZ WorkoutHistory helpers — pure module.
//
// Extracted from WorkoutHistory.js as part of the pre-Slice-C refactor split
// (Phase 1). Everything here is state-free and closure-free so it can be
// unit-tested directly and re-used by upcoming Slice C export code without
// dragging in the full History component.
// ─────────────────────────────────────────────────────────────────────────────

import { Download } from 'lucide-react';

// Slice C decimation cap — Strava's TCX limit is ~10 MB. At ~250 bytes per
// Trackpoint that's ~40k samples before the file itself blows the limit,
// but Intervals.icu chokes above ~5-6k points on long sessions and mobile
// browsers hitch on the base64 encode step. 1800 samples is roughly one
// point every 2s for a 60-minute row, which preserves shape without ballooning.
const TCX_SERIES_CAP = 1800;

/**
 * Evenly downsample a series to at most `cap` samples. Keeps the first and
 * last point so start/finish times are exact. Returns the input untouched
 * if it's already under the cap.
 */
export const decimateSeries = (arr, cap = TCX_SERIES_CAP) => {
  if (!Array.isArray(arr) || arr.length <= cap) return arr || [];
  const stride = arr.length / cap;
  const out = [];
  for (let i = 0; i < cap - 1; i++) {
    out.push(arr[Math.floor(i * stride)]);
  }
  out.push(arr[arr.length - 1]);
  return out;
};

// Build the TCX payload shape from a workout doc. Centralised so download +
// upload paths stay in sync — used by the History page AND the Slice C
// per-row export button. Decimates long series (1Hz sampling can produce
// 5k+ points on a 90-minute row) so the TCX stays under the size limits
// Strava / Intervals.icu enforce.
export const tcxPayloadFromWorkout = (workout) => ({
  startTime: new Date(workout.start_time),
  duration: workout.duration,
  distance: workout.distance,
  calories: workout.calories,
  avgHeartRate: workout.avg_heart_rate,
  maxHeartRate: workout.max_heart_rate,
  avgStrokeRate: workout.avg_stroke_rate,
  avgWatts: workout.avg_watts,
  sessionName: workout.session_name || 'CREWZ Workout',
  boatType: workout.boat_type || '',
  heartRateData: decimateSeries(workout.heart_rate_data || []),
  gpsData: decimateSeries(workout.gps_data || []),
  // Pieces become laps inside the single session activity
  laps: (workout.child_sessions || []).filter((c) => c.type === 'piece'),
});

// Export platforms configuration. `fit` is the legacy id — it now emits TCX.
export const EXPORT_PLATFORMS = [
  { id: 'fit', name: 'Download TCX File', icon: Download, color: 'text-zinc-700', bgColor: 'bg-zinc-100' },
  { id: 'strava', name: 'Strava', icon: null, color: 'text-orange-600', bgColor: 'bg-orange-100', requiresConnection: true },
  { id: 'intervals', name: 'Intervals.icu', icon: null, color: 'text-purple-600', bgColor: 'bg-purple-100', requiresConnection: true },
  { id: 'garmin', name: 'Garmin Connect', icon: null, color: 'text-blue-600', bgColor: 'bg-blue-100', mocked: true },
  { id: 'trainingpeaks', name: 'TrainingPeaks', icon: null, color: 'text-red-600', bgColor: 'bg-red-100', mocked: true },
  { id: 'googlefit', name: 'Google Fit', icon: null, color: 'text-green-600', bgColor: 'bg-green-100', mocked: true },
  { id: 'rowsandall', name: 'RowsandAll', icon: null, color: 'text-cyan-600', bgColor: 'bg-cyan-100', mocked: true },
];

// HR Zone colours and names — energy system designations.
export const HR_ZONES = [
  { zone: 'UT2', name: 'Utilization 2',     color: 'bg-blue-500',   textColor: 'text-blue-600' },
  { zone: 'UT1', name: 'Utilization 1',     color: 'bg-green-500',  textColor: 'text-green-600' },
  { zone: 'AT',  name: 'Aerobic Threshold', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  { zone: 'TR',  name: 'Transport',         color: 'bg-orange-500', textColor: 'text-orange-600' },
  { zone: 'AN',  name: 'Anaerobic',         color: 'bg-red-500',    textColor: 'text-red-600' },
];

// Available metrics for the History detail row (per-workout swappable tiles).
export const DETAIL_METRICS = [
  { id: 'stroke_rate',    abbr: 'SR',  label: 'Stroke Rate',    unit: 'spm' },
  { id: 'avg_watts',      abbr: 'W',   label: 'Avg Watts',      unit: 'W' },
  { id: 'total_strokes',  abbr: 'ST',  label: 'Total Strokes',  unit: '' },
  { id: 'max_hr',         abbr: 'MHR', label: 'Max HR',         unit: 'bpm' },
  { id: 'avg_split',      abbr: 'SPL', label: 'Avg Split',      unit: '/500m' },
  { id: 'distance',       abbr: 'DST', label: 'Distance',       unit: 'm' },
  { id: 'calories',       abbr: 'CAL', label: 'Calories',       unit: 'kcal' },
  { id: 'duration',       abbr: 'DUR', label: 'Duration',       unit: '' },
  { id: 'avg_hr',         abbr: 'HR',  label: 'Avg HR',         unit: 'bpm' },
];

// ─── Session-type badges (CrewZ v1.6 §3.1) ─────────────────────────────────
export const SESSION_TYPE_BADGES = {
  standard: { label: 'Standard', className: 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300' },
  distance: { label: 'Distance', className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  race:     { label: 'Race',     className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' },
  program:  { label: 'Program',  className: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
};

export const resolveSessionType = (workout) => {
  if (workout?.session_type && SESSION_TYPE_BADGES[workout.session_type]) return workout.session_type;
  // Legacy inference — the compatibility read layer.
  if (workout?.program_id || workout?.program_name) return 'program';
  if (Array.isArray(workout?.splits_500m) && workout.splits_500m.length > 0) return 'race';
  if (workout?.target_distance) return 'distance';
  return 'standard';
};

// ─── Filter windows for the History top chips ──────────────────────────────
export const FILTER_WINDOWS = [
  { id: 'all',   label: 'All time' },
  { id: 'week',  label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'year',  label: 'This year' },
];

/**
 * Window start (inclusive) for a given filter id, in local time so
 * "This week" respects the rower's Monday.
 */
export const windowStart = (windowId, now = new Date()) => {
  if (windowId === 'week') {
    const d = new Date(now);
    // Monday-anchored week (ISO). If today is Sunday (0), roll back 6 days;
    // otherwise roll back (day-1).
    const day = d.getDay();
    const back = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - back);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (windowId === 'month') return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  if (windowId === 'year')  return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
  return null; // all-time
};

// ─── Formatters ────────────────────────────────────────────────────────────
export const formatDuration = (seconds) => {
  if (!seconds) return '0h 0m';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

export const formatDurationFull = (seconds) => {
  if (!seconds) return '00:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const formatDistance = (meters) => {
  if (!meters) return '0m';
  if (meters >= 1000) return (meters / 1000).toFixed(1) + ' km';
  return Math.floor(meters) + 'm';
};

export const formatDistanceMeters = (meters) => {
  if (!meters) return '0m';
  return Math.floor(meters).toLocaleString() + 'm';
};

export const formatSplit = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-CA'); // YYYY-MM-DD

export const formatTime = (dateString) =>
  new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });

// ─── Row helpers ───────────────────────────────────────────────────────────
/**
 * Approximate HR zone distribution used by the legacy summary rendering when
 * `workout.hr_zones` isn't supplied. Slice A's real zone maths lives in
 * `lib/hrZones.js`; keep this bucket here for the History fallback only.
 */
export const getHRZones = (workout) => {
  if (workout.hr_zones) return workout.hr_zones;
  const avgHR = workout.avg_heart_rate || 140;
  if (avgHR < 120) return [40, 35, 15, 8, 2];
  if (avgHR < 140) return [15, 30, 35, 15, 5];
  if (avgHR < 160) return [5, 15, 45, 30, 5];
  return [2, 8, 25, 45, 20];
};

export const isProgramWorkout = (workout) => !!(workout?.program_id || workout?.program_name);

/**
 * Prefer the stored session_name (v1.6). Fall back to a distance-based
 * heuristic only for legacy rows that never had a name saved.
 */
export const getWorkoutTitle = (workout) => {
  if (workout?.session_name && String(workout.session_name).trim()) {
    return workout.session_name.trim();
  }
  if (workout?.program_name) return workout.program_name;
  if (workout?.distance >= 10000) return '10K Row';
  if (workout?.distance >= 5000) return '5K Steady State';
  if (workout?.distance >= 2000) return '2K Piece';
  return 'Training Session';
};
